import { getConcept } from "./concepts.js";
import { loadPlaylist, safeUrl } from "./live-data.js";
/**
 * Mount the shared player inside a React-owned element.
 * @param {HTMLElement} root
 * @param {{search?: string, api: Object, onStatus?: (value: string) => void, onHeight?: (value: number) => void}} config
 */
export function mountPlayer(
  root,
  { search = "", api, onStatus = () => {}, onHeight = () => {} },
) {
  const q = (id) => root.querySelector(`#${id}`);
  const params = new URLSearchParams(search);
  let concept = getConcept(params.get("concept"));
  let clips = [],
    campaign = null,
    activeIndex = 0,
    activeFilter = "trending",
    dismissed = false,
    ending = false,
    clock,
    remaining = 8;
  let controller,
    generation = 0;
  const options = {
    playlist: params.get("playlist") || "",
    geo: params.has("geo") ? params.get("geo") : "DE",
    region: params.get("region") || "",
    previewCampaignId: params.get("previewCampaignId") || "",
  };
  const video = q("video");
  function notify(type, value) {
    if (type === "hltv-demo-height") onHeight(value);
    if (type === "hltv-demo-status") onStatus(value);
  }
  const heightObserver = new ResizeObserver(() =>
    notify("hltv-demo-height", q("player").offsetHeight),
  );
  heightObserver.observe(q("player"));
  function status(value) {
    onStatus(value);
  }
  function time(sec) {
    return `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(
      2,
      "0",
    )}`;
  }
  function duration() {
    return Number.isFinite(video.duration) ? video.duration : 20;
  }
  function apex() {
    return duration() * 0.65;
  }
  function slot(name) {
    return campaign?.slots?.[name];
  }
  function image(url, alt) {
    const img = document.createElement("img");
    img.src = safeUrl(url);
    img.alt = alt || "";
    img.loading = "lazy";
    return img;
  }
  function setText(root, selector, value) {
    root.querySelectorAll(selector).forEach((el) => {
      el.textContent = value || "";
      el.hidden = !value;
    });
  }
  function creative(root, name) {
    const data = slot(name);
    root.dataset.slot = name;
    root.querySelectorAll(".creative-image").forEach((el) => el.remove());
    root.classList.remove("image-only", "has-creative");
    if (!data) return;
    setText(root, ".brand-name", campaign.brandName);
    setText(root, ".brand-line", data.title);
    setText(root, ".brand-title", data.title);
    setText(root, ".brand-detail", data.subtitle);
    setText(root, ".brand-cta", data.ctaLabel || "Learn more");
    const mark = root.querySelector(".brand-mark");
    if (mark) mark.replaceChildren();
    const url = safeUrl(data.assetUrl);
    if (url) {
      const img = image(url, data.altText || campaign.brandName);
      img.className = "creative-image";
      if (!data.title && !data.ctaLabel) {
        root.classList.add("image-only");
        root.insertBefore(img, root.firstChild);
      } else if (mark) mark.append(img);
    }
    const color = data.ctaBgColor || campaign.theme?.primary || "#82afd0";
    const textColor =
      data.ctaTextColor || campaign.theme?.primaryText || "#fff";
    root.querySelectorAll(".cta").forEach((el) => {
      el.style.backgroundColor = color;
      el.style.color = textColor;
    });
  }
  function paintEndcard() {
    const root = q("endcard"),
      data = slot("endcard"),
      art = root.querySelector(".end-art");
    root.dataset.slot = "endcard";
    root.classList.remove("has-creative");
    root.classList.toggle("no-campaign", !data);
    art.querySelectorAll("img").forEach((img) => img.remove());
    art.removeAttribute("href");
    art.hidden = !data;
    setText(
      root,
      ".brand-title",
      data?.title || (data ? campaign.brandName : "Clip finished"),
    );
    setText(root, ".brand-cta", data?.ctaLabel || "Learn more ↗");
    setText(
      root,
      ".takeover-label",
      data ? `Sponsored · ${campaign.brandName}` : "",
    );
    if (!data) return;
    const destination = safeUrl(data.ctaUrl);
    if (destination) art.href = destination;
    art.setAttribute(
      "aria-label",
      `${campaign.brandName}: ${data.title || "Visit sponsor"}`,
    );
    const cta = root.querySelector(".cta");
    cta.style.backgroundColor =
      data.ctaBgColor ||
      slot("topBanner")?.ctaBgColor ||
      campaign.theme?.primary ||
      "#435971";
    cta.style.color = data.ctaTextColor || "#fff";
    if (safeUrl(data.assetUrl)) {
      const img = image(data.assetUrl, data.altText || campaign.brandName);
      img.className = "creative-image";
      img.loading = "eager";
      root.classList.add("has-creative");
      art.append(img);
      img.onerror = () => {
        if (!art.contains(img)) return;
        root.classList.remove("has-creative");
        img.remove();
      };
    }
  }
  function paint() {
    q("player").dataset.concept = concept.id;
    root.style.setProperty("--accent", campaign?.theme?.primary || "#82afd0");
    const corner = q("corner"),
      logo = slot("upperLeftLogo");
    corner.dataset.slot = "upperLeftLogo";
    corner.replaceChildren();
    if (logo) {
      if (safeUrl(logo.assetUrl))
        corner.append(image(logo.assetUrl, logo.altText || campaign.brandName));
      else corner.textContent = campaign.brandName;
    }
    corner.hidden = !logo || !["control", "endcard"].includes(concept.id);
    creative(q("offer"), "topBanner");
    creative(q("railAd"), "playlistBanner");
    creative(q("nativeAd"), "playlistBanner");
    paintEndcard();
    q("railAd").hidden =
      !slot("playlistBanner") || !["control", "endcard"].includes(concept.id);
    q("nativeAd").hidden = !slot("playlistBanner") || concept.id !== "native";
    q("apexMarker").hidden = concept.id !== "earned";
    q("apexMarker").title = "Demo apex timing";
    update();
    updateScroll();
  }
  function update() {
    const t = video.currentTime;
    q("time").textContent = `${time(t)} - ${
      Number.isFinite(video.duration) ? time(video.duration) : "–:––"
    }`;
    q("seek").value = (t / duration()) * 100;
    q("seek").style.setProperty("--progress", `${q("seek").value}%`);
    const earnedVisible = concept.id === "earned" && t >= apex() + 0.9;
    const persistent =
      ["control", "endcard"].includes(concept.id) || earnedVisible;
    q("corner").hidden = !slot("upperLeftLogo") || !persistent;
    q("railAd").hidden = !slot("playlistBanner") || !persistent;
    const threshold =
      concept.id === "earned"
        ? apex() + 0.9
        : concept.id === "consolidation"
        ? 4
        : (slot("topBanner")?.appearAfterMs ?? 5000) / 1000;
    q("offer").hidden =
      !slot("topBanner") ||
      ending ||
      dismissed ||
      !["control", "consolidation", "earned"].includes(concept.id) ||
      t < threshold;
    q("dismiss").hidden = slot("topBanner")?.dismissable === false;
    q("dismiss").classList.toggle(
      "ready",
      t >= threshold + (slot("topBanner")?.dismissTimeoutMs ?? 3000) / 1000,
    );
    q("play").textContent = video.paused ? "▶" : "Ⅱ";
    q("play").setAttribute("aria-label", video.paused ? "Play" : "Pause");
    q("bigPlay").hidden = !clips.length || !video.paused || ending || t > 0;
  }
  function clearEnd() {
    ending = false;
    clearInterval(clock);
    q("endcard").hidden = true;
    q("upNext").hidden = true;
    q("player").classList.remove("showing-up-next");
    updateRowStates();
  }
  function play() {
    if (clips.length) video.play().catch(() => update());
  }
  function restart(auto = true) {
    clearEnd();
    dismissed = false;
    video.currentTime = 0;
    if (auto) play();
    else video.pause();
    update();
  }
  function selectClip(index, auto = true) {
    if (!clips.length) return;
    activeIndex = (index + clips.length) % clips.length;
    const clip = clips[activeIndex];
    clearEnd();
    dismissed = false;
    video.pause();
    video.hidden = false;
    video.src = clip.url;
    video.poster = clip.thumbnail;
    video.load();
    q("clipLabel").textContent = clip.player;
    q("video").setAttribute("aria-label", clip.title);
    updateRowStates();
    update();
    if (auto) play();
  }
  function visibleIndices() {
    return clips.flatMap((clip, index) =>
      activeFilter === "trending" ||
      (activeFilter === "aces" ? clip.ace : clip.fourk)
        ? [index]
        : [],
    );
  }
  function nextIndex() {
    const visible = visibleIndices();
    if (!visible.length) return (activeIndex + 1) % clips.length;
    const position = visible.indexOf(activeIndex);
    return visible[(position + 1) % visible.length];
  }
  function updateRowStates() {
    const next = nextIndex();
    root.querySelectorAll("[data-clip]").forEach((row) => {
      const index = Number(row.dataset.clip),
        active = index === activeIndex;
      row.classList.toggle("active", active);
      row.classList.toggle("played", active && ending);
      row.setAttribute("aria-current", String(active));
      const state = row.querySelector(".row-state");
      state.textContent = active
        ? ending
          ? "PLAYED"
          : "LIVE"
        : index === next
        ? "▸ NEXT"
        : "";
      state.hidden = !state.textContent;
    });
  }
  function updateScroll() {
    const list = q("clipList");
    q("scrollPlaylist").hidden =
      list.scrollHeight <= list.clientHeight + list.scrollTop + 2;
  }
  function applyFilter() {
    const visible = visibleIndices();
    root.querySelectorAll("[data-clip]").forEach((row) => {
      row.hidden = !visible.includes(Number(row.dataset.clip));
    });
    root.querySelectorAll("[data-filter]").forEach((button) => {
      const filter = button.dataset.filter;
      const count =
        filter === "trending"
          ? clips.length
          : clips.filter((clip) => (filter === "aces" ? clip.ace : clip.fourk))
              .length;
      button.querySelector("strong").textContent = count;
      button.setAttribute("aria-pressed", String(filter === activeFilter));
    });
    q("emptyPlaylist").hidden = !!visible.length || !clips.length;
    q(
      "playlistCount",
    ).textContent = `${visible.length} of ${clips.length} highlights`;
    q("clipList").scrollTop = 0;
    updateRowStates();
    if (ending && !slot("endcard")) paintUpNext();
    updateScroll();
  }
  function formatViews(views) {
    return new Intl.NumberFormat("en", {
      notation: "compact",
      maximumFractionDigits: 1,
    })
      .format(views)
      .toLowerCase();
  }
  function teamBadge(name, url) {
    const badge = document.createElement("span");
    badge.className = "team-badge";
    badge.title = name;
    badge.setAttribute("aria-label", name);
    badge.textContent = name.slice(0, 1);
    if (url) {
      const logo = image(url, "");
      logo.onload = () => badge.classList.add("has-image");
      logo.onerror = () => logo.remove();
      badge.append(logo);
    }
    return badge;
  }
  function renderPlaylist() {
    root.querySelectorAll("[data-clip]").forEach((el) => el.remove());
    const list = q("clipList");
    clips.forEach((clip, index) => {
      const row = document.createElement("button");
      row.className = "clip-row";
      row.dataset.clip = index;
      row.title = clip.title;
      const portrait = document.createElement("span");
      portrait.className = "player-avatar";
      portrait.setAttribute("aria-label", clip.player || "Player");
      if (clip.avatar) {
        const avatar = image(clip.avatar, clip.player);
        avatar.onload = () => portrait.classList.add("has-image");
        avatar.onerror = () => avatar.remove();
        portrait.append(avatar);
      }
      const copy = document.createElement("span"),
        title = document.createElement("b"),
        detail = document.createElement("span");
      copy.className = "clip-copy";
      title.textContent = clip.title;
      detail.className = "clip-meta";
      const views = document.createElement("span");
      views.className = "view-count";
      views.textContent = `↗ ${formatViews(clip.views)}`;
      views.title = `${clip.views.toLocaleString("en")} views`;
      views.setAttribute("aria-label", views.title);
      detail.append(views);
      if (clip.team) detail.append(teamBadge(clip.team, clip.teamLogo));
      if (clip.team && clip.opponent) {
        const vs = document.createElement("span");
        vs.textContent = "vs";
        detail.append(vs);
      }
      if (clip.opponent)
        detail.append(teamBadge(clip.opponent, clip.opponentLogo));
      if (clip.round !== null) {
        const round = document.createElement("span");
        round.className = "round-number";
        round.textContent = `Round ${clip.round}`;
        detail.append(round);
      }
      const state = document.createElement("span");
      state.className = "row-state";
      copy.append(title, detail);
      row.append(portrait, copy, state);
      row.onclick = () => selectClip(index);
      list.append(row);
      if (index === 0) list.append(q("nativeAd"));
    });
    q("player").querySelector(".playlist").prepend(q("railAd"));
    applyFilter();
  }
  root.querySelectorAll("[data-filter]").forEach((button) => {
    button.onclick = () => {
      activeFilter = button.dataset.filter;
      applyFilter();
    };
  });
  q("scrollPlaylist").onclick = () =>
    q("clipList").scrollBy({
      top: q("clipList").clientHeight * 0.75,
      behavior: "smooth",
    });
  q("clipList").addEventListener("scroll", updateScroll);
  const listObserver = new ResizeObserver(updateScroll);
  listObserver.observe(q("clipList"));
  async function refresh() {
    if (!api) return;
    controller?.abort();
    controller = new AbortController();
    const request = ++generation;
    clearEnd();
    video.pause();
    video.removeAttribute("src");
    video.removeAttribute("poster");
    video.load();
    video.hidden = true;
    q("clipLabel").textContent = "";
    q("playlistCount").textContent = "LIVE PLAYLIST";
    clips = [];
    campaign = null;
    root.querySelectorAll("[data-clip]").forEach((el) => el.remove());
    applyFilter();
    paint();
    q("mediaError").textContent = "Loading live playlist…";
    q("mediaError").hidden = false;
    status("Loading live playlist…");
    try {
      const result = await loadPlaylist(api, options, controller.signal);
      if (request !== generation) return;
      clips = result.clips;
      campaign = result.campaign;
      renderPlaylist();
      paint();
      selectClip(0, false);
      q("mediaError").hidden = true;
      status("");
    } catch (error) {
      if (request !== generation || error.name === "AbortError") return;
      q("mediaError").textContent = error.message;
      q("mediaError").hidden = false;
      status(error.message);
    }
  }
  function paintUpNext() {
    const next = nextIndex();
    const clip = clips[next];
    const hasNext = next !== activeIndex;
    q("upNextLabel").textContent = hasNext ? "UP NEXT" : "WATCH AGAIN";
    q("upNextTitle").textContent = clip.title;
    q("upNextMeta").textContent = `${formatViews(clip.views)} views`;
    const art = q("upNextArt");
    art.replaceChildren();
    if (clip.thumbnail) {
      const thumbnail = image(clip.thumbnail, "");
      thumbnail.loading = "eager";
      thumbnail.onerror = () => thumbnail.remove();
      art.append(thumbnail);
    }
    q("playNext").hidden = !hasNext;
    drawUpNextCountdown();
    updateRowStates();
  }
  function drawUpNextCountdown() {
    const hasNext = nextIndex() !== activeIndex;
    q("upNextCountdown").textContent = hasNext
      ? `Playing in ${remaining}s`
      : "";
    q("upNextProgress").style.width = hasNext
      ? `${(remaining / 3) * 100}%`
      : "0%";
  }
  function showUpNext() {
    remaining = 3;
    q("upNext").hidden = false;
    q("player").classList.add("showing-up-next");
    paintUpNext();
    clearInterval(clock);
    clock = setInterval(() => {
      if (
        document.hidden ||
        nextIndex() === activeIndex ||
        q("upNext").contains(document.activeElement)
      )
        return;
      remaining--;
      drawUpNextCountdown();
      if (remaining <= 0) selectClip(nextIndex());
    }, 1000);
  }
  function showEnd() {
    if (!clips.length) return;
    clearEnd();
    video.pause();
    ending = true;
    q("offer").hidden = true;
    q("bigPlay").hidden = true;
    if (!slot("endcard")) {
      showUpNext();
      return;
    }
    q("endcard").hidden = false;
    remaining =
      concept.id === "endcard"
        ? 8
        : concept.id === "consolidation"
        ? 7
        : Math.max(
            1,
            Math.round((slot("endcard")?.autoAdvanceMs || 5000) / 1000),
          );
    const total = remaining;
    function drawCountdown() {
      q("countdown").textContent = remaining;
      q("countdownRing").style.strokeDashoffset =
        113.1 * (1 - remaining / total);
      q("next").setAttribute("aria-label", `Next clip in ${remaining} seconds`);
    }
    drawCountdown();
    clearInterval(clock);
    clock = setInterval(() => {
      if (
        document.hidden ||
        q("endcard").matches(":hover") ||
        q("endcard").contains(document.activeElement)
      )
        return;
      remaining--;
      drawCountdown();
      if (remaining <= 0) selectClip(nextIndex());
    }, 1000);
  }
  function jump(phase) {
    if (!clips.length) return;
    clearEnd();
    dismissed = false;
    if (phase === "endcard") {
      showEnd();
      return;
    }
    const t =
      phase === "offer"
        ? concept.id === "earned"
          ? apex() + 1
          : (concept.id === "consolidation"
              ? 4
              : (slot("topBanner")?.appearAfterMs ?? 5000) / 1000) +
            (slot("topBanner")?.dismissTimeoutMs ?? 3000) / 1000 +
            0.2
        : 0;
    if (video.readyState >= 1) {
      video.currentTime = Math.min(t, Math.max(0, duration() - 0.1));
      video.pause();
      update();
    }
  }
  q("play").onclick = q("bigPlay").onclick = () => {
    if (ending) restart();
    else if (video.paused) play();
    else video.pause();
  };
  video.onclick = () => q("play").click();
  function paintMute() {
    q(
      "mute",
    ).innerHTML = `<svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" fill="currentColor"><path d="M1 6h3l4-4v12l-4-4H1z"/>${
      video.muted
        ? '<path d="m11 5 4 6m0-6-4 6" fill="none" stroke="currentColor" stroke-width="1.5"/>'
        : '<path d="M11 4q5 4 0 8M10 6q2 2 0 4" fill="none" stroke="currentColor" stroke-width="1.5"/>'
    }</svg>`;
    q("mute").setAttribute("aria-label", video.muted ? "Unmute" : "Mute");
  }
  q("mute").onclick = () => {
    video.muted = !video.muted;
    paintMute();
  };
  paintMute();
  q("seek").oninput = () => {
    if (!clips.length) return;
    clearEnd();
    video.currentTime = (Number(q("seek").value) / 100) * duration();
    update();
  };
  q("dismiss").onclick = () => {
    dismissed = true;
    update();
  };
  q("next").onclick = q("playNext").onclick = () => selectClip(nextIndex());
  q("replayClip").onclick = () => restart();
  q("replay").onclick = () => restart();
  q("full").onclick = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else
      q("player")
        .requestFullscreen?.()
        .catch(() => {});
  };
  q("playlistToggle").onclick = () => {
    const open = q("player").classList.toggle("list-open");
    q("playlistToggle").setAttribute("aria-expanded", String(open));
    q("playlistToggle").lastElementChild.textContent = open
      ? "Close playlist −"
      : "Open playlist ＋";
  };
  function openOffer(event) {
    if (event.target.closest("#dismiss")) return;
    const root = event.currentTarget.closest("[data-slot]");
    const url = safeUrl(slot(root?.dataset.slot)?.ctaUrl);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  }
  root
    .querySelectorAll(".sponsor-action")
    .forEach((el) => (el.onclick = openOffer));
  q("offer").onclick = (event) => {
    if (event.target.classList.contains("creative-image")) openOffer(event);
  };
  ["timeupdate", "play", "pause", "loadedmetadata"].forEach((event) =>
    video.addEventListener(event, update),
  );
  video.addEventListener("ended", showEnd);
  video.addEventListener("error", () => {
    if (!clips.length) return;
    q("mediaError").textContent =
      "This clip could not load. Select another highlight.";
    q("mediaError").hidden = false;
  });
  video.addEventListener("loadeddata", () => (q("mediaError").hidden = true));
  function command(action, value) {
    if (action === "concept") {
      concept = getConcept(value);
      clearEnd();
      dismissed = false;
      paint();
    }
    if (action === "geo") {
      options.geo = String(value || "");
      options.region = "";
      refresh();
    }
    if (action === "jump") jump(value);
  }
  paint();
  refresh();
  return {
    command,
    destroy() {
      generation++;
      controller?.abort();
      clearInterval(clock);
      heightObserver.disconnect();
      listObserver.disconnect();
      video.pause();
      video.removeAttribute("src");
      video.load();
      root.replaceChildren();
    },
  };
}
